import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgentesService } from './data-access/services/agents.service';
import { Agent } from './data-access/models/agent.model';
import { ModalMessageComponent } from '../../shared/ui/components/modal-message/modal-message.component';
import { ModalConfirmComponent, ConfirmButton } from '../../shared/ui/components/modal-confirm/modal-confirm.component';
import { AgentType } from '../agent-types/data-access/models/agent-type.model';

@Component({
  selector: 'app-agentes',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalMessageComponent, ModalConfirmComponent],
  templateUrl: './agents.component.html',
  styles: []
})
export class AgentesComponent implements OnInit {
  agents: Agent[] = [];
  tiposAgente: AgentType[] = [];
  versiones: string[] = [];
  selectedAgent: Agent | null = null;

  selectedTipo: number | null = null;
  selectedPromptVersion: string = '';
  promptContent: string = '';
  promptReporting: string = '';
  selectedMinScore: string = '0';

  // Modal states
  showMessageModal = false;
  messageModalTitle = '';
  messageModalMessage = '';
  messageModalType: 'success' | 'error' | 'info' | 'warning' = 'info';

  showConfirmModal = false;
  confirmModalTitle = '';
  confirmModalMessage = '';
  confirmModalType: 'danger' | 'warning' | 'info' = 'info';
  confirmButtons: ConfirmButton[] = [];
  changeContentFlag = false;
  optionsPrompts: any[] = [];

  constructor(
    private agentesService: AgentesService
  ) { }

  ngOnInit() {
    this.agentesService.getPromptsOptions().subscribe({
      next: (res) => {
        this.optionsPrompts = res;
        this.tiposAgente = res.filter(t => t.is_active).map(t => t.agent_type);
        if (this.tiposAgente.length > 0) {
          this.selectedTipo = this.tiposAgente.find(type => type.id === 1)?.id || this.tiposAgente[0].id;
          this.versiones = [...new Set(this.optionsPrompts.filter(option => option.agent_type_id === this.selectedTipo).map(a => a.version))];
          this.loadAgentsByType(this.selectedTipo);
        }
      },
      error: (error) => {
        console.error('Error cargando tipos de agentes:', error);
      }
    });
  }

  loadAgentsByType(type: number) {
    this.agentesService.getAgentes(type).subscribe({
      next: (agents) => {
        this.agents = agents;

        if (agents.length > 0) {
          this.loadAgent(agents[0]);
        }

      },
      error: (error) => {
        console.error('Error cargando agentes:', error);
        this.showMessage('¡Vaya!', 'No se ha encontrado ningún prompt activo para el tipo seleccionado.', 'error');
      }
    });
  }

  get hasChanges(): boolean {
    if (!this.selectedAgent) return false;

    return this.selectedAgent.content !== this.promptContent ||
      (this.selectedAgent.prompt_manager || '') !== this.promptReporting ||
      this.selectedAgent.min_score !== this.selectedMinScore;
  }

  loadAgent(agent: Agent) {
    this.selectedAgent = agent;
    this.selectedTipo = agent.agent_type.id;
    this.selectedPromptVersion = agent.version;
    this.promptContent = agent.content;
    this.promptReporting = agent.prompt_manager || '';
    this.selectedMinScore = agent.min_score;
  }

  onTipoChange() {
    this.versiones = [...new Set(this.optionsPrompts.filter(option => option.agent_type_id === this.selectedTipo).map(a => a.version))];
    this.loadAgentsByType(this.selectedTipo!);
  }

  changeContentPrompt() {
    this.changeContentFlag = true;
  }

  onVersionChange() {
    this.promptContent = '';
    this.promptReporting = '';
    this.selectedMinScore = '';
    this.getPrompt();
  }

  getPrompt() {
    this.agentesService.getAgentByTypeVersion(this.selectedTipo!, this.selectedPromptVersion).subscribe({
      next: (agents) => {
        if (agents.length > 0) {
          this.loadAgent(agents[0]);
        }
      },
      error: (error) => {
        console.error('Error cargando agente por tipo y versión:', error);
      }
    });
  }

  onSaveClick() {
    if (!this.selectedAgent) {
      console.error('No hay agente seleccionado');
      return;
    }

    this.confirmModalTitle = this.changeContentFlag ? '¿Guardar los cambios?' : '¿Cómo deseas guardar los cambios?';
    this.confirmModalMessage = this.changeContentFlag ? 'Al cambiar el contenido del prompt se creará una nueva versión.' : 'Elige si deseas crear una nueva versión o actualizar la versión actual del agente.';
    this.confirmModalType = 'info';
    if (this.changeContentFlag) {
      this.confirmButtons = [
        { text: 'Cancelar', action: 'cancel' },
        { text: 'Crear nueva versión', action: 'create', style: 'success' }
      ];
      this.changeContentFlag = false;
    } else {
      this.confirmButtons = [
        { text: 'Cancelar', action: 'cancel' },
        { text: 'Editar actual', action: 'edit', style: 'warning' },
        { text: 'Crear nueva versión', action: 'create', style: 'success' }
      ];
    }
    this.showConfirmModal = true;
  }

  onConfirmAction(action: string) {
    this.closeConfirmModal();

    if (action === 'create') {
      this.createNewVersion();
    } else if (action === 'edit') {
      this.updateCurrentVersion();
    }
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
    this.confirmButtons = [];
  }

  createNewVersion() {
    if (!this.selectedAgent) {
      return;
    }

    this.agentesService.createNewVersion(
      this.selectedAgent.id,
      12,
      this.promptContent,
      this.promptReporting || null,
      this.selectedMinScore
    ).subscribe({
      next: (res) => {
        this.versiones.push(res.version);
        this.selectedPromptVersion = res.version;
        this.showMessage('Éxito', 'Nueva versión creada correctamente', 'success');
        // this.ngOnInit();
      },
      error: (error) => {
        console.error('Error creando nueva versión:', error);
        this.showMessage('Error', 'No se pudo crear la nueva versión', 'error');
      }
    });
  }

  updateCurrentVersion() {
    if (!this.selectedAgent) {
      return;
    }

    this.agentesService.updateAgent(
      this.selectedAgent.id,
      this.promptContent,
      this.promptReporting || '',
      this.selectedMinScore
    ).subscribe({
      next: (updatedAgent) => {
        this.showMessage('Éxito', 'Versión actualizada correctamente', 'success');
        // this.ngOnInit();
      },
      error: (error) => {
        console.error('Error actualizando versión:', error);
        this.showMessage('Error', 'No se pudo actualizar la versión', 'error');
      }
    });
  }

  showMessage(title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') {
    this.messageModalTitle = title;
    this.messageModalMessage = message;
    this.messageModalType = type;
    this.showMessageModal = true;
  }

  closeMessageModal() {
    this.showMessageModal = false;
  }
}
